import AddTeamModal from '../teams/AddTeamModal';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, Col, Dropdown, Form, Row, Spinner } from 'react-bootstrap';
import Icon from '@mdi/react';
import { mdiAccountMultiplePlusOutline } from '@mdi/js';
import axios from 'axios';

import JoinTeamModal from '../profile/JoinTeamModal';
import {
  buildSeasonOptions,
  fetchActiveSeasons,
  getSeasonOptionLabel,
  pickDefaultSeasonForUser,
} from '../utils/activeSeasonUtil';

const TeamActionsToggle = forwardRef(({ children, onClick, className }, ref) => {
  const safeClassName = (className || '')
    .split(' ')
    .filter((name) => name && name !== 'dropdown-toggle')
    .join(' ');

  return (
    <button
      type="button"
      ref={ref}
      className={`btn btn-sm btn-secondary ${safeClassName}`.trim()}
      onClick={(event) => {
        event.preventDefault();
        onClick(event);
      }}
    >
      <span className="d-inline-flex align-items-center">
        <span>Team Actions</span>
        <i className="bi bi-chevron-down ms-2"></i>
      </span>
    </button>
  );
});

TeamActionsToggle.displayName = 'TeamActionsToggle';

function UserTeams() {
  const [teams, setTeams] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
  const [defaultLeagueId, setDefaultLeagueId] = useState(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);

  const handleShow = () => setModalVisible(true);
  const handleClose = () => setModalVisible(false);

  const fetchTeams = useCallback(async (seasonId) => {
    if (!seasonId) {
      setTeams([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/api/teams/user-teams/', {
        params: seasonId === 'all' ? {} : { season: seasonId },
        withCredentials: true,
      });
      setTeams(response.data);
      setError(null);
    } catch (fetchError) {
      console.error('Error fetching teams:', fetchError);
      setError('Unable to fetch teams.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPageData = async () => {
      try {
        setLoading(true);
        const [detailsResponse, activeSeasons, userLeaguesResponse] = await Promise.all([
          axios.get('/api/users/my-details/', {
            withCredentials: true,
          }),
          fetchActiveSeasons(),
          axios.get('/api/leagues/user-leagues/', {
            withCredentials: true,
          }),
        ]);

        if (!isMounted) {
          return;
        }

        const { first_name, last_name, players = [] } = detailsResponse.data;
        setPlayerName(`${first_name} ${last_name}`);
        const playerIds = players.map((player) => player.id);
        const allSeasonOptions = buildSeasonOptions(
          activeSeasons,
          userLeaguesResponse.data,
          { playerIds, registeredOnly: true }
        );
        setSeasonOptions(allSeasonOptions);

        const defaultSeason = pickDefaultSeasonForUser(allSeasonOptions, playerIds);
        if (defaultSeason) {
          setDefaultLeagueId(defaultSeason.league);
          setSelectedSeason(defaultSeason.id.toString());
          setSelectedLeagueId(defaultSeason.league);
          setError(null);
        } else {
          setTeams([]);
          setError(null);
          setSelectedSeason('all');
          setDefaultLeagueId(null);
          setSelectedLeagueId(null);
          setLoading(false);
        }
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        console.error('Error loading user teams page:', loadError);
        setError('Unable to load team information.');
        setLoading(false);
      }
    };

    loadPageData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSeason) {
      return;
    }
    const season = seasonOptions.find((option) => option.id.toString() === selectedSeason);
    setSelectedLeagueId(season?.league ?? defaultLeagueId ?? null);
    fetchTeams(selectedSeason);
  }, [defaultLeagueId, fetchTeams, seasonOptions, selectedSeason]);

  const handleTeamAdded = async () => {
    setModalVisible(false);
    await fetchTeams(selectedSeason);
  };

  const handleJoinTeamSuccess = async () => {
    await fetchTeams(selectedSeason);
  };

  if (loading) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="container p-0">
      <Card>
        <Card.Header className="border-0 pb-0">
          <Row className="align-items-start g-3">
            <Col xs={12}>
              <h2 className="h4 card-title mb-1">Teams for {playerName}</h2>
              <p className="mb-0 text-muted">View your teams for the selected season.</p>
            </Col>
            <Col xs={12} className="d-flex justify-content-end">
              <Dropdown>
                <Dropdown.Toggle as={TeamActionsToggle} id="user-team-actions">
                  Team Actions
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      handleShow();
                    }}
                  >
                    <i className="fa-solid fa-plus ps-1 pe-2"></i> Create Team
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowJoinTeamModal(true);
                    }}
                  >
                    <Icon path={mdiAccountMultiplePlusOutline} size={1} /> Join a Team
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          <Row className="mb-4">
            <Col md={6} lg={4}>
              <Form.Group controlId="user-team-season-select">
                <Form.Label>Season</Form.Label>
                <Form.Select
                  value={selectedSeason}
                  onChange={(event) => setSelectedSeason(event.target.value)}
                >
                  <option value="all">All Seasons</option>
                  {seasonOptions.map((season) => (
                    <option key={season.id} value={season.id}>
                      {getSeasonOptionLabel(season)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="row g-4">
            {teams.map((team) => (
              <div key={team.id} className="col-sm-6 col-xl-4">
                <Link to={`/teams/${team.id}`} className="text-decoration-none">
                  <Card className="h-100">
                    <div className="position-relative">
                      <img
                        className="img-fluid rounded-top"
                        src={team.logo || '/static/images/kb_static/lemont_rec_leagues_circle.png'}
                        alt={`${team.name} logo`}
                        style={{
                          width: '200px',
                          height: '200px',
                          objectFit: 'cover',
                          margin: 'auto',
                          display: 'block',
                        }}
                      />
                    </div>
                    <Card.Body className="position-relative pt-0">
                      <h6 className="mt-3 text-dark">{team.name}</h6>
                      <p className="mb-0 small text-muted">
                        <i className="bi bi-trophy pe-1"></i> Wins: {team.wins}
                      </p>
                      <p className="small text-muted">
                        <i className="bi bi-trophy-fill pe-1"></i> Losses: {team.losses}
                      </p>
                      <p className="small text-muted">
                        <i className="bi bi-trophy-fill pe-1"></i> Draws: {team.draws}
                      </p>
                    </Card.Body>
                  </Card>
                </Link>
              </div>
            ))}
            {teams.length === 0 && (
              <div className="col-12">
                <div className="text-center text-muted py-4">
                  No teams found for this season.
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <AddTeamModal
        show={modalVisible}
        handleClose={handleClose}
        onTeamAdded={handleTeamAdded}
      />

      <JoinTeamModal
        isOpen={showJoinTeamModal}
        onClose={(success) => {
          setShowJoinTeamModal(false);
          if (success) {
            handleJoinTeamSuccess();
          }
        }}
        leagueId={selectedLeagueId || defaultLeagueId}
        seasonId={selectedSeason !== 'all' ? selectedSeason : null}
      />
    </div>
  );
}

export default UserTeams;
